/**
 * Serviço de API
 * Configuração do Axios e endpoints da API
 * 
 * @author Equipe GestaoeCRM
 * @date 25/09/2025
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

// Configurar base URL - Detecta automaticamente o host correto
const getApiBaseUrl = (): string => {
  // Se VITE_API_URL estiver definido, usar ele
  const envApiUrl = (import.meta as any).env?.VITE_API_URL;
  if (envApiUrl) {
    // Garantir que termina com /api se não tiver
    if (envApiUrl.endsWith('/api')) {
      return envApiUrl;
    } else if (!envApiUrl.endsWith('/')) {
      return `${envApiUrl}/api`;
    } else {
      return `${envApiUrl}api`;
    }
  }
  
  // Caso contrário, detectar baseado no host atual
  const currentHost = window.location.hostname;
  const apiPort = '3001';
  
  return `http://${currentHost}:${apiPort}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// DEBUG: Log da URL base configurada
// console.log('🔍 [API] Base URL configurada:', API_BASE_URL);

// Criar instância do Axios
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Aumentado para 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
  // Configurações para lidar com conexões instáveis
  maxRedirects: 5,
  maxContentLength: 50 * 1024 * 1024, // 50MB
});

// Interceptor para requests
api.interceptors.request.use(
  (config) => {
    // NÃO enviar token para rotas de autenticação
    if (!config.url?.includes('/auth/login') && !config.url?.includes('/auth/refresh')) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Se a requisição contém FormData, remover TODOS os Content-Type e transformers
    if (config.data instanceof FormData) {
      // Remover Content-Type do headers
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
      
      // Limpar transformRequest para não interferir com FormData
      config.transformRequest = [];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para responses
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Se erro 401 e não é uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post('/auth/refresh', {
            refreshToken,
          });

          if (response.data.success) {
            const { accessToken } = response.data.data;
            
            // Salvar novo token
            localStorage.setItem('accessToken', accessToken);
            
            // Retry da requisição original
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh token inválido, fazer logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Tratar erros de conexão com retry
    if (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_RESET') {
      // console.log('🔄 Erro de conexão detectado, tentando novamente...');
      
      // Se não foi uma tentativa de retry, tentar novamente
      if (originalRequest && !originalRequest._connectionRetry) {
        originalRequest._connectionRetry = true;
        
        // Aguardar um pouco antes de tentar novamente
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // console.log('🔄 Tentando requisição novamente...');
        return api(originalRequest);
      } else {
        toast.error('Erro de conexão. Tente novamente em alguns segundos.');
      }
    } else if (error.code === 'ERR_NETWORK' || error.message?.includes('CORS')) {
      toast.error('Erro de conexão. Verifique se o servidor está rodando.');
    } else if (error.response && error.response.status >= 500) {
      toast.error('Erro interno do servidor. Tente novamente.');
    } else if (error.response?.status === 404) {
      toast.error('Recurso não encontrado.');
    } else if (error.response?.status === 403) {
      toast.error('Acesso negado.');
    } else if (error.response?.status === 429) {
      toast.error('Muitas requisições. Tente novamente mais tarde.');
    } else if (!error.response) {
      toast.error('Erro de conexão. Verifique sua internet.');
    }

    return Promise.reject(error);
  }
);

// Função para definir token de autenticação
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// API de Autenticação
export const authApi = {
  login: (email: string, password: string) => {
    // console.log('🔍 [AUTH] Tentando login com:', { email, password: '***' });
    // console.log('🔍 [AUTH] Base URL configurada:', API_BASE_URL);
    // console.log('🔍 [AUTH] URL completa esperada:', `${API_BASE_URL}/auth/login`);
    // console.log('🔍 [AUTH] Axios baseURL:', api.defaults.baseURL);
    return api.post('/auth/login', { email, password });
  },
  
  logout: () => api.post('/auth/logout'),
  
  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  getMe: () => api.get('/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  
  setAuthToken,
};

// API de Usuários
export const usersApi = {
  getAll: (params?: any) => api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  activate: (id: string) => api.put(`/users/${id}/activate`),
  getStats: () => api.get('/users/stats/overview'),
};

// API de Projetos
export const projectsApi = {
  getAll: (params?: any) => api.get('/projects', { params }),
  getById: (id: string) => api.get(`/projects/${id}`),
  create: (data: any) => api.post('/projects', data),
  update: (id: string, data: any) => api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  getStats: () => api.get('/projects/stats/overview'),
};

// API de Demandas
export const demandsApi = {
  getAll: (params?: any) => api.get('/demands', { params }),
  getById: (id: string) => api.get(`/demands/${id}`),
  create: (data: any) => api.post('/demands', data),
  update: (id: string, data: any) => api.put(`/demands/${id}`, data),
  delete: (id: string) => api.delete(`/demands/${id}`),
  assign: (id: string, assigneeId: string) => 
    api.put(`/demands/${id}/assign`, { assigneeId }),
  replicate: (id: string, frequency: 'mensal' | 'semanal') =>
    api.post(`/demands/${id}/replicate`, { frequency }),
  replicateManual: (id: string, title: string) =>
    api.post(`/demands/${id}/replicate-manual`, { title }),
  transfer: (id: string, areaId: string) =>
    api.post(`/demands/${id}/transfer`, { areaId }),
  getStats: () => api.get('/demands/stats/overview'),
};

// API de Jobs/Tasks
export const osApi = {
  getAll: (params?: any) => api.get('/os', { params }),
  getById: (id: string) => api.get(`/os/${id}`),
  create: (data: any) => api.post('/os', data),
  update: (id: string, data: any) => api.put(`/os/${id}`, data),
  delete: (id: string) => api.delete(`/os/${id}`),
  // Endpoints de estatísticas administrativas
  getAreaStats: (page: number = 1) => api.get(`/os/admin/areas-stats?page=${page}`),
  getProjectsByArea: (areaId: string) => api.get(`/os/admin/areas/${areaId}/projects`),
  getDemandsByProject: (projectId: string) => api.get(`/os/admin/projects/${projectId}/demands`),
};

// API de Sessões
export const sessionsApi = {
  start: (osId: string, description?: string) =>
    api.post('/sessions/start', { osId, description }),
  
  stop: (sessionId?: string, description?: string) =>
    api.post('/sessions/stop', { sessionId, description }),
  
  getActive: () => api.get('/sessions/active'),
  
  getAll: (params?: any) => api.get('/sessions', { params }),
  
  getStats: (params?: any) => api.get('/sessions/stats', { params }),
  
  distributeTime: (distributions: any[]) =>
    api.post('/sessions/distribute-time', { distributions }),
};

// API de Checklist
export const checklistApi = {
  getByOS: (demandId: string) => api.get(`/checklist/demand/${demandId}`),
  create: (data: any) => api.post('/checklist', data),
  update: (id: string, data: any) => api.put(`/checklist/${id}`, data),
  delete: (id: string) => api.delete(`/checklist/${id}`),
};

// API de Relatórios
export const reportsApi = {
  getHoursByAnalyst: (params?: any) => 
    api.get('/reports/hours-by-analyst', { params }),
  
  getProjectProgress: (params?: any) => 
    api.get('/reports/project-progress', { params }),
  
  getHoursByProject: (params?: any) =>
    api.get('/reports/hours-by-project', { params }),
  
  getAnalystPerformance: (params?: any) =>
    api.get('/reports/analyst-performance', { params }),
  
  exportHoursByProjectCSV: (params?: any) =>
    api.get('/reports/hours-by-project/export-csv', { 
      params,
      responseType: 'blob'
    }),
  
  exportAnalystPerformanceCSV: (params?: any) =>
    api.get('/reports/analyst-performance/export-csv', {
      params,
      responseType: 'blob'
    }),
  
  getOSByArea: (params?: any) =>
    api.get('/reports/os-by-area', { params }),
  
  getOSCompleted: () =>
    api.get('/reports/os-completed'),
  
  getUsersByArea: (params?: any) =>
    api.get('/reports/users-by-area', { params }),
};

// API de Permissões de Menu
export const menuPermissionApi = {
  getAll: (params?: any) => api.get('/menu-permissions', { params }),
  getByRole: (role: string) => api.get(`/menu-permissions/role/${role}`),
  getCurrent: () => api.get('/menu-permissions/current'),
  create: (data: any) => api.post('/menu-permissions', data),
  update: (id: string, data: any) => api.put(`/menu-permissions/${id}`, data),
  delete: (id: string) => api.delete(`/menu-permissions/${id}`),
};

// API do Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getChartByArea: () => api.get('/dashboard/charts/by-area'),
  getChartByStatus: () => api.get('/dashboard/charts/by-status'),
  getChartToday: () => api.get('/dashboard/charts/today'),
};

// API de Demandas (função helper para CreateOSModal)
export const listDemands = async (params?: any) => {
  const response = await api.get('/demands', { params });
  return response.data;
};

// API de Tenants e Uso
export const tenantApi = {
  getAllTenants: () => api.get('/tenants'),
  getUsage: (tenantId: string) => api.get(`/tenants/${tenantId}/usage`),
  getMonthlyUsageReport: (tenantId: string, year?: number, month?: number) => {
    const params: any = {};
    if (year) params.year = year;
    if (month) params.month = month;
    return api.get(`/tenants/${tenantId}/usage/monthly`, { params });
  },
  getUsageChartData: (tenantId: string, months?: number) => {
    const params: any = {};
    if (months) params.months = months;
    return api.get(`/tenants/${tenantId}/usage/chart`, { params });
  },
  getAvailableMonthlyReports: (tenantId: string) => 
    api.get(`/tenants/${tenantId}/usage/reports`),
};

// API de Planos
export const plansApi = {
  getAll: () => api.get('/plans'),
  getAvailableFeatures: () => api.get('/plans/available-features'),
};

// API de Assinaturas e Pagamentos
export const subscriptionsApi = {
  getAll: () => api.get('/subscriptions'),
  getById: (id: string) => api.get(`/subscriptions/${id}`),
  createCheckout: (data: { planId: string; successUrl: string; cancelUrl: string }) =>
    api.post('/subscriptions/checkout', data),
  changePlan: (subscriptionId: string, newPlanId: string) =>
    api.patch(`/subscriptions/${subscriptionId}/change-plan`, { newPlanId }),
  cancel: (subscriptionId: string, cancelAtPeriodEnd: boolean = true) =>
    api.post(`/subscriptions/${subscriptionId}/cancel`, { cancelAtPeriodEnd }),
  resume: (subscriptionId: string) =>
    api.post(`/subscriptions/${subscriptionId}/resume`),
  getPayments: (params?: { page?: number; limit?: number }) =>
    api.get('/subscriptions/payments', { params }),
  getPaymentById: (id: string) =>
    api.get(`/subscriptions/payments/${id}`),
};

// API de Guest Checkout (compras sem cadastro prévio)
export const guestCheckoutApi = {
  createCheckout: (data: {
    email: string;
    name: string;
    phone?: string;
    planId: string;
    successUrl: string;
    cancelUrl: string;
  }) => api.post('/guest-checkout', data),
  verifyToken: (token: string) => api.get(`/guest-checkout/verify-token/${token}`),
  checkEmail: (email: string) => api.get(`/guest-checkout/check-email/${encodeURIComponent(email)}`),
  register: (data: {
    token: string;
    cnpj: string;
    companyName: string;
    tradeName?: string;
    password: string;
    addressStreet?: string;
    addressNumber?: string;
    addressComplement?: string;
    addressNeighborhood?: string;
    addressCity?: string;
    addressState?: string;
    addressZipcode?: string;
  }) => api.post('/guest-checkout/register', data),
};

// API do Dashboard Global (Super Admin)
export const globalDashboardApi = {
  getStats: () => api.get('/global-dashboard/stats'),
  getPlanDistribution: () => api.get('/global-dashboard/plan-distribution'),
  getMonthlyGrowth: () => api.get('/global-dashboard/monthly-growth'),
  getTenantUsage: (limit?: number) => 
    api.get('/global-dashboard/tenant-usage', { params: { limit } }),
  getSystemHealth: () => api.get('/global-dashboard/system-health'),
};

// API de Gestão de Planos (Super Admin)
export const planManagementApi = {
  getAllPlans: () => api.get('/plan-management/plans'),
  getPlanById: (id: string) => api.get(`/plan-management/plans/${id}`),
  createPlan: (data: any) => api.post('/plan-management/plans', data),
  updatePlan: (id: string, data: any) => api.put(`/plan-management/plans/${id}`, data),
  togglePlanStatus: (id: string, active: boolean) => 
    api.patch(`/plan-management/plans/${id}/status`, { active }),
  getAvailableFeatures: () => api.get('/plan-management/available-features'),
  updatePlanFeatures: (id: string, features: string[]) => 
    api.put(`/plan-management/plans/${id}/features`, { features }),
};

// API de Gestão Avançada de Tenants (Super Admin)
export const tenantManagementApi = {
  getTenantDetails: (id: string) => api.get(`/tenant-management/tenants/${id}/details`),
  getTenantLogs: (id: string, params?: { page?: number; limit?: number; type?: string }) =>
    api.get(`/tenant-management/tenants/${id}/logs`, { params }),
  getTenantPerformance: (id: string, period?: number) =>
    api.get(`/tenant-management/tenants/${id}/performance`, { params: { period } }),
  getTenantsComparison: (tenantIds: string[]) =>
    api.get('/tenant-management/tenants/comparison', { params: { tenantIds: tenantIds.join(',') } }),
  getTenantUsageStats: (id: string, months?: number) =>
    api.get(`/tenant-management/tenants/${id}/usage-stats`, { params: { months } }),
  updateTenantPlan: (id: string, planId: string) =>
    api.patch(`/tenant-management/tenants/${id}/plan`, { planId }),
};

// API de Gerenciamento de Trial
export const trialApi = {
  getTrialStatus: (tenantId: string) => api.get(`/tenants/${tenantId}/trial/status`),
  updateTrialDays: (tenantId: string, trialDays: number) => 
    api.patch(`/tenants/${tenantId}/trial/days`, { trialDays }),
  extendTrial: (tenantId: string, additionalDays: number, reason?: string) => 
    api.post(`/tenants/${tenantId}/trial/extend`, { additionalDays, reason }),
};

// API de Relatórios Administrativos (Super Admin)
export const adminReportsApi = {
  getBillingReport: (params?: { startDate?: string; endDate?: string; tenantId?: string; planId?: string }) =>
    api.get('/admin-reports/billing', { params }),
  getRevenueReport: (params?: { startDate?: string; endDate?: string; tenantId?: string; planId?: string }) =>
    api.get('/admin-reports/revenue', { params }),
  getPaymentReport: (params?: { startDate?: string; endDate?: string; tenantId?: string; planId?: string; status?: string }) =>
    api.get('/admin-reports/payments', { params }),
  getSubscriptionReport: (params?: { startDate?: string; endDate?: string; tenantId?: string; planId?: string; status?: string }) =>
    api.get('/admin-reports/subscriptions', { params }),
  getTenantBillingReport: (tenantId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/admin-reports/tenant-billing/${tenantId}`, { params }),
  exportReport: (data: { type: string; format: string; startDate?: string; endDate?: string; tenantId?: string; planId?: string }) =>
    api.post('/admin-reports/export', data),
  getPlansStats: (params?: { planType?: string }) =>
    api.get('/admin-reports/plans-stats', { params }),
  getUsersInactivityReport: (params?: { days?: number; tenantId?: string; planType?: string }) =>
    api.get('/admin-reports/users-inactivity', { params }),
  getUsersLongSessionReport: () =>
    api.get('/admin-reports/users-long-sessions'),
  forceLogoutUser: (userId: string) =>
    api.post(`/admin-reports/users/${userId}/force-logout`),
  sendInactivityEmail: (data: { userIds: string[]; days?: number }) =>
    api.post('/admin-reports/users-inactivity/send-email', data),
};

// API de Funcionalidades Avançadas (Super Admin)
export const advancedFeaturesApi = {
  getSystemAlerts: (params?: { type?: string; tenantId?: string }) =>
    api.get('/advanced-features/alerts', { params }),
  createAlert: (data: { type: string; title: string; message: string; tenantId?: string; metadata?: any }) =>
    api.post('/advanced-features/alerts', data),
  getAuditLogs: (params?: { startDate?: string; endDate?: string; tenantId?: string; type?: string; page?: number; limit?: number }) =>
    api.get('/advanced-features/audit-logs', { params }),
  getSystemPerformance: () =>
    api.get('/advanced-features/performance'),
  getTenantFilters: (params?: { tenantId?: string; startDate?: string; endDate?: string; planId?: string; status?: string }) =>
    api.get('/advanced-features/filters', { params }),
  getAdvancedAnalytics: (params?: { startDate?: string; endDate?: string; tenantId?: string; planId?: string }) =>
    api.get('/advanced-features/analytics', { params }),
};

// Exportar instância padrão
export default api;
